import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';

const DISCORD_URL = 'https://discord.gg/ubnZnbsvCv';

const REASON_TEXT = {
  kick_access_denied: "You cancelled the Kick login, or denied the permission request.",
  invalid_state: "Your sign-in link expired or was already used. This can happen if you waited too long on Kick's page, or opened the link a second time.",
};
function describeReason(reason) {
  if (!reason) return 'Something went wrong while signing you in.';
  if (REASON_TEXT[reason]) return REASON_TEXT[reason];
  if (reason.startsWith('kick_')) return `Kick returned an error: ${reason.slice(5)}`;
  if (reason.includes('token exchange failed')) return "Kick rejected the login request from our server.";
  if (reason.includes('user lookup failed')) return "Signed in with Kick, but couldn't load your Kick profile afterward.";
  if (reason.includes('access_token')) return "Kick didn't return the expected login data.";
  if (reason.includes('username')) return "Kick didn't return a username for your account.";
  return reason;
}

export default function LoginErrorModal() {
  const [params, setParams] = useSearchParams();
  const code = params.get('loginError');
  const reason = params.get('reason');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (code) setCopied(false);
  }, [code]);

  if (!code) return null;

  function close() {
    const next = new URLSearchParams(params);
    next.delete('loginError');
    next.delete('reason');
    setParams(next, { replace: true });
  }

  const details = [
    `Error code: ${code}`,
    `Reason: ${reason || 'unknown'}`,
    `Time: ${new Date().toISOString()}`,
    `Page: ${window.location.href}`,
    `Browser: ${navigator.userAgent}`,
  ].join('\n');

  async function copy() {
    try {
      await navigator.clipboard.writeText(details);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API unavailable — the text is still selectable in the box below
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,.72)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-[#24242e] p-6"
        style={{ background: 'linear-gradient(180deg,#14141c,#0c0c12)' }}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="text-xl font-black text-white">Sign-in failed</div>
          <button onClick={close} className="text-[#888899] hover:text-white text-xl leading-none cursor-pointer">×</button>
        </div>

        <p className="text-sm text-white/80 mb-4">{describeReason(reason)}</p>

        <div className="text-xs text-[#7d7d8c] uppercase tracking-wider mb-1.5">Error code</div>
        <div className="rounded-lg bg-[#181820] border border-[#353535] px-3 py-2.5 font-mono-brand text-sm text-[#ff544a] mb-4 break-all">
          {code}
        </div>

        <p className="text-sm text-white/70 mb-3">
          Copy this error and send it in{' '}
          <a href={DISCORD_URL} target="_blank" rel="noreferrer" className="text-[#5865F2] font-bold hover:underline">
            our Discord
          </a>{' '}
          so we can look into it.
        </p>

        <pre className="rounded-lg bg-[#0e0e14] border border-[#24242e] px-3 py-2.5 text-[11px] text-[#888899] whitespace-pre-wrap break-all mb-4 select-all">
          {details}
        </pre>

        <button onClick={copy} className="btn-primary w-full rounded-lg py-3 text-sm">
          {copied ? 'Copied!' : 'Copy error details'}
        </button>
      </div>
    </div>,
    document.body
  );
}
